import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Search, Eye, Pencil, Trash2, Printer, ArrowRight, Stethoscope, Save } from "lucide-react";
import { toast } from "sonner";

const empty = (patient_id = "", prescription_id = "") => ({
  patient_id,
  prescription_id,
  referred_to_name: "",
  referred_to_specialty: "",
  referred_to_clinic: "",
  referred_to_contact: "",
  reason_for_referral: "",
  clinical_summary: "",
  date: new Date().toISOString().slice(0, 10),
  status: "active",
});

export function ReferralDialog({ open, onOpenChange, patientOptions = [], initial = null, onSaved, fixedPatientId = "", fixedPrescriptionId = "" }) {
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [partners, setPartners] = useState([]);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [savingAsPartner, setSavingAsPartner] = useState(false);
  const partnerRef = useRef(null);

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({ ...empty(), ...initial, date: (initial.date || "").slice(0, 10) });
      } else {
        setForm(empty(fixedPatientId, fixedPrescriptionId));
      }
      api.get("/referral-partners").then((r) => setPartners(r.data || [])).catch(() => {});
    }
  }, [open, initial, fixedPatientId, fixedPrescriptionId]);

  useEffect(() => {
    const onDoc = (e) => { if (partnerRef.current && !partnerRef.current.contains(e.target)) setPartnerOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const update = (patch) => setForm((p) => ({ ...p, ...patch }));

  const filteredPartners = useMemo(() => {
    const v = (form.referred_to_name || "").toLowerCase();
    return (partners || []).filter((p) => {
      if (!v) return true;
      return (p.full_name || "").toLowerCase().includes(v)
        || (p.specialty || "").toLowerCase().includes(v)
        || (p.clinic || "").toLowerCase().includes(v);
    }).slice(0, 8);
  }, [form.referred_to_name, partners]);

  const pickPartner = (p) => {
    update({
      referred_to_name: p.full_name || "",
      referred_to_specialty: p.specialty || "",
      referred_to_clinic: p.clinic || "",
      referred_to_contact: p.contact || "",
    });
    setPartnerOpen(false);
  };

  const saveAsPartner = async () => {
    if (!form.referred_to_name?.trim()) { toast.error("Enter a doctor name first"); return; }
    setSavingAsPartner(true);
    try {
      const { data } = await api.post("/referral-partners", {
        full_name: form.referred_to_name.trim(),
        specialty: form.referred_to_specialty || "",
        clinic: form.referred_to_clinic || "",
        contact: form.referred_to_contact || "",
      });
      setPartners((prev) => [...prev, data].sort((a, b) => (a.full_name || "").localeCompare(b.full_name || "")));
      toast.success("Saved to referral partners");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setSavingAsPartner(false);
    }
  };

  const submit = async () => {
    if (!form.patient_id) { toast.error("Select a patient"); return; }
    if (!form.referred_to_name?.trim()) { toast.error("Enter referred-to doctor name"); return; }
    setSaving(true);
    try {
      const payload = { ...form, date: form.date ? new Date(form.date).toISOString() : undefined };
      let saved;
      if (initial?.id) {
        const { data } = await api.put(`/referrals/${initial.id}`, payload);
        saved = data;
        toast.success("Referral updated");
      } else {
        const { data } = await api.post("/referrals", payload);
        saved = data;
        toast.success("Referral created");
      }
      onOpenChange(false);
      if (onSaved) onSaved(saved);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const knownPartner = (partners || []).some(
    (p) => (p.full_name || "").trim().toLowerCase() === (form.referred_to_name || "").trim().toLowerCase()
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="referral-dialog">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Edit Referral" : "New Referral"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Patient *</Label>
            {fixedPatientId ? (
              <Input value={patientOptions.find((p) => p.id === fixedPatientId)?.full_name || "(selected)"} readOnly />
            ) : (
              <Select value={form.patient_id} onValueChange={(v) => update({ patient_id: v })}>
                <SelectTrigger data-testid="ref-patient"><SelectValue placeholder="Select patient" /></SelectTrigger>
                <SelectContent>
                  {patientOptions.map((p) => (<SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="col-span-2 relative" ref={partnerRef}>
            <div className="flex items-center justify-between">
              <Label>Referred-to Doctor *</Label>
              <span className="text-[11px] text-slate-500">Type to search saved partners or enter a new name</span>
            </div>
            <Input
              value={form.referred_to_name}
              onChange={(e) => { update({ referred_to_name: e.target.value }); setPartnerOpen(true); }}
              onFocus={() => setPartnerOpen(true)}
              placeholder="Search or type doctor name..."
              data-testid="ref-doctor"
              autoComplete="off"
            />
            {partnerOpen && filteredPartners.length > 0 ? (
              <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-auto">
                {filteredPartners.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => pickPartner(p)}
                    className="block w-full text-left px-3 py-2 hover:bg-blue-50"
                    data-testid={`partner-option-${p.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <Stethoscope size={12} className="text-blue-700" />
                      <span className="font-medium text-slate-900 text-sm">{p.full_name}</span>
                      {p.specialty ? <span className="text-xs text-slate-500">· {p.specialty}</span> : null}
                    </div>
                    {p.clinic ? <div className="text-xs text-slate-500 mt-0.5 ml-5">{p.clinic}</div> : null}
                  </button>
                ))}
              </div>
            ) : null}
            {form.referred_to_name && !knownPartner ? (
              <button
                type="button"
                onClick={saveAsPartner}
                disabled={savingAsPartner}
                className="mt-1 text-[11px] text-blue-700 hover:underline inline-flex items-center gap-1"
                data-testid="btn-save-as-partner"
              >
                <Save size={11} /> Save "{form.referred_to_name.trim()}" as a referral partner for next time
              </button>
            ) : null}
          </div>
          <div><Label>Specialty</Label><Input value={form.referred_to_specialty} onChange={(e) => update({ referred_to_specialty: e.target.value })} placeholder="Cardiology, Endocrinology..." data-testid="ref-specialty" /></div>
          <div><Label>Contact</Label><Input value={form.referred_to_contact} onChange={(e) => update({ referred_to_contact: e.target.value })} placeholder="Phone / email" /></div>
          <div className="col-span-2"><Label>Clinic / Hospital</Label><Input value={form.referred_to_clinic} onChange={(e) => update({ referred_to_clinic: e.target.value })} /></div>
          <div><Label>Date</Label><Input type="date" value={form.date || ""} onChange={(e) => update({ date: e.target.value })} /></div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => update({ status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Reason for Referral *</Label><Textarea rows={2} value={form.reason_for_referral} onChange={(e) => update({ reason_for_referral: e.target.value })} data-testid="ref-reason" /></div>
          <div className="col-span-2"><Label>Clinical Summary / History / Findings</Label><Textarea rows={5} value={form.clinical_summary} onChange={(e) => update({ clinical_summary: e.target.value })} placeholder="Relevant history, exam findings, lab results..." /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-blue-600 hover:bg-blue-700" disabled={saving} data-testid="ref-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Referrals() {
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const nav = useNavigate();

  const load = async (search = "") => {
    const [r, p] = await Promise.all([
      api.get(`/referrals${search ? `?q=${encodeURIComponent(search)}` : ""}`),
      api.get("/patients"),
    ]);
    setItems(Array.isArray(r.data) ? r.data : []);
    setPatients(Array.isArray(p.data) ? p.data : []);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => load(q), 250); return () => clearTimeout(t); }, [q]);

  const del = async (id) => {
    if (!window.confirm("Delete this referral?")) return;
    await api.delete(`/referrals/${id}`);
    toast.success("Referral deleted");
    load(q);
  };

  const openEdit = (r) => { setEditing(r); setOpen(true); };
  const openNew = () => { setEditing(null); setOpen(true); };

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="referrals-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Work Sans" }}>Referrals</h1>
          <p className="text-slate-500 text-sm mt-1">{items.length} referral{items.length === 1 ? "" : "s"} on file</p>
        </div>
        <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-new-referral">
          <Plus size={14} className="mr-2" /> New Referral
        </Button>
      </div>

      <Card className="p-4 border-slate-200 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search by patient or referred-to doctor..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="ref-search" />
        </div>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Date</th>
                <th className="px-4 py-3 text-left font-semibold">Patient</th>
                <th className="px-4 py-3 text-left font-semibold">Referred To</th>
                <th className="px-4 py-3 text-left font-semibold">Reason</th>
                <th className="px-4 py-3 text-left font-semibold">Attached Rx</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-400">No referrals yet.</td></tr>
              ) : items.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`referral-row-${r.id}`}>
                  <td className="px-4 py-3 text-xs text-slate-700">{(r.date || "").slice(0, 10) || "—"}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{r?.patient_snapshot?.full_name || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{r.referred_to_name}</div>
                    <div className="text-xs text-slate-500">{r.referred_to_specialty}{r.referred_to_clinic ? ` · ${r.referred_to_clinic}` : ""}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-700 max-w-[280px] truncate">{r.reason_for_referral}</td>
                  <td className="px-4 py-3 text-xs">
                    {r.prescription_id ? (
                      <Link to={`/prescriptions/${r.prescription_id}/preview`} className="text-blue-700 hover:underline flex items-center gap-1">
                        View Rx <ArrowRight size={10} />
                      </Link>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${r.status === "completed" ? "bg-green-50 text-green-700" : r.status === "cancelled" ? "bg-slate-100 text-slate-500" : "bg-blue-50 text-blue-700"}`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => nav(`/referrals/${r.id}/preview`)} data-testid={`referral-view-${r.id}`}><Eye size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`referral-edit-${r.id}`}><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => window.open(`/referrals/${r.id}/preview?print=1`, "_self")} data-testid={`referral-print-${r.id}`}><Printer size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => del(r.id)} data-testid={`referral-delete-${r.id}`}><Trash2 size={14} className="text-red-600" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <ReferralDialog open={open} onOpenChange={setOpen} patientOptions={patients} initial={editing} onSaved={() => load(q)} />
    </div>
  );
}
